import { useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Box, Button, Flex, Heading, Text } from '@opendoor/bricks/core';
import SelectiveSpritesheet from '@opendoor/bricks/core/Icon/SelectiveSpritesheet';
import CloseIcon from '@opendoor/bricks/core/Icon/SpritesheetIcons/Close';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import dynamic from 'next/dynamic';
import NextImage from 'next/image';

import { EntryComponent } from '../../cms/entries/entries';
import AddressSearch from '../../components/shared/AddressSearch';
import { ILpComponentVideoHero } from '../../declarations/contentful';
import { imgixLoader } from '../../helpers/imgixUrl';
import Grid, { gridStyles } from '../landing-pages-v2/Grid';
import { StickyCTAUtility } from '../landing-pages-v2/StickyCTAUtility/StickyCTAUtility';
import {
  generateDeprecatedImgixPictureSet,
  generateDeprecatedPictureSizeSet,
} from '../shared/performance/pictures';

const Vimeo = dynamic(() => import('@u-wave/react-vimeo'));
const FullScreenModal = dynamic(() =>
  import('@opendoor/bricks/complex/Modal').then((mod) => mod.FullScreenModal),
);

const Video = styled.video<{ initialPoster?: string; initialPosterDesktop?: string }>((props) => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: '540px 540px 36px 36px',
  aspectRatio: '4 / 5',
  background: `transparent url("${props.initialPoster}") 50% 50% / cover no-repeat`,
  '@media screen and (min-width: 769px)': {
    background: `transparent url("${props.initialPosterDesktop}") 50% 50% / cover no-repeat`,
  },
  '@media (min-width: 1336px)': {
    maxHeight: '804px',
  },
}));

const RenderVideoHeroCard = (entry: ILpComponentVideoHero) => {
  const { fields } = entry;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openVideoModalHandler = () => {
    setIsModalOpen(true);
  };

  const {
    ref: addressEntryRef,
    inView: addressEntryInView,
    entry: intersectionObserverEntry,
  } = useInView();
  // For the first intersection observer event, the entry is undefined
  // as the element loads. We want to make sure not to flash the sticky
  // cta while it loads for the first time.
  const showStickyCTAUtility =
    Boolean(intersectionObserverEntry) && !addressEntryInView && !isModalOpen;

  const { isSmallMobile, isMobile, isTablet, isDesktop } = useDevice();

  const videoPlayerWidth = () => {
    if (isSmallMobile) {
      return '290px';
    } else if (isMobile) {
      return '310px';
    } else if (isTablet) {
      return '740px';
    } else if (isDesktop) {
      return '1100px';
    }
    return '1300px';
  };

  const videoPlayerHeight = () => {
    if (isSmallMobile) {
      return '150px';
    } else if (isMobile) {
      return '200px';
    } else if (isTablet) {
      return '416px';
    }
    return '600px';
  };

  return (
    <Box
      analyticsName={`cosmos-landing-page-video-hero-${fields?.analyticsId}`}
      trackImpression
      inViewThreshold={0.5}
    >
      <SelectiveSpritesheet icons={[CloseIcon]} />
      <StickyCTAUtility analyticsId={fields?.analyticsId} isVisible={showStickyCTAUtility} />
      <FullScreenModal
        onRequestClose={() => setIsModalOpen(false)}
        analyticsName={`cosmos-landing-page-video-hero-modal-${fields?.analyticsId}`}
        isOpen={isModalOpen}
        headerIconButtonVariant="icon"
        title="How it works"
        themeProps={{ theme: 'novo' }}
      >
        <Flex
          flexDirection="column"
          height={['600px', null, '100%']}
          width="100%"
          justifyContent="center"
          alignItems="center"
        >
          {fields?.showAddressInputCta && (
            <Box
              width={['100%', null, '100%', '500px']}
              paddingTop={6}
              paddingBottom={[6, null, 8]}
              maxWidth={[null, '420px', null]}
              minWidth={[null, null, '335px']}
              className="hp-reskin-address-entry-wrapper"
            >
              <AddressSearch
                hideLabel
                inputLocation="hero-cta"
                trackingTaxonomy="new_homepage"
                actionText={fields?.addressCtaText || 'Get cash offer'}
                actionTextSmall={fields?.addressCtaTextSmall || 'Get offer'}
                placeholderText={fields?.addressCtaPlaceholderText}
                showShadow={false}
              />
            </Box>
          )}
          <Vimeo
            keyboard
            width={videoPlayerWidth()}
            height={videoPlayerHeight()}
            video="https://vimeo.com/726130027" // "how it works" vimeo video
          />
        </Flex>
      </FullScreenModal>
      <Grid mr={[6, null, 8]}>
        <Box gridColumn={gridStyles.colSpan12} paddingTop={[0, null, 8]}>
          <Flex
            zIndex="1"
            flexDirection={['column', null, 'row']}
            justifyContent="space-between"
            width="100%"
            columnGap={[null, null, 4]}
            position="relative"
          >
            <Flex
              id="title-and-address-entry"
              flexDirection="column"
              mt={[5]}
              width={['100%', null, '50%']}
              mb={[10, null, 8]}
            >
              <Flex
                marginTop={[0, null, 8, 12]}
                flexDirection="column"
                marginBottom={[4, null, 9]}
                marginLeft={0}
                width="100%"
                alignItems={['center', null, 'unset']}
              >
                <Heading
                  as="h1"
                  color="neutrals100"
                  marginTop={[6, null, 11, 0]}
                  fontWeight="medium"
                  marginBottom={[0, null, 3]}
                  textAlign={['center', null, 'left']}
                  maxWidth={['275px', '305px', '480px', '543px']}
                  css={css`
                    line-height: 100%;
                    font-size: 48px;
                    letter-spacing: -0.03em;
                    @media (min-width: 376px) and (max-width: 768px) {
                      font-size: 64px;
                      line-height: 100%;
                      letter-spacing: -0.034em;
                    }
                    @media (min-width: 769px) and (max-width: 1048px) {
                      font-size: 56px;
                      line-height: 104%;
                      letter-spacing: -0.034em;
                    }
                    @media (min-width: 789px) and (max-width: 1048px) {
                      max-width: 320px;
                    }
                    @media (min-width: 1049px) and (max-width: 1440px) {
                      font-size: 96px;
                      line-height: 95%;
                      letter-spacing: -0.034em;
                    }
                    @media (min-width: 1441px) {
                      line-height: 95%;
                      font-size: 112px;
                      letter-spacing: -0.034em;
                    }
                  `}
                >
                  {fields?.title}
                </Heading>
                <Text
                  as="h2"
                  size="160"
                  color="neutrals100"
                  marginTop={5}
                  marginBottom={0}
                  textAlign={['center', null, 'left']}
                  maxWidth={[null, '235px', '211px', '313px']}
                >
                  {fields?.subtitle}
                </Text>
              </Flex>
              <Box visibility="hidden" ref={addressEntryRef} />
              <Flex justifyContent={['center', null, 'flex-start']}>
                <Box
                  width={['100%', null, '100%', '500px']}
                  paddingTop={[6, null, 'unset']}
                  maxWidth={[null, '420px', null]}
                  minWidth={[null, null, '335px']}
                  zIndex={10}
                  className="hp-reskin-address-entry-wrapper"
                >
                  <AddressSearch
                    hideLabel
                    inputLocation="hero-cta"
                    trackingTaxonomy="new_homepage"
                    actionText={fields?.addressCtaText || 'Get cash offer'}
                    actionTextSmall={fields?.addressCtaTextSmall || 'Get offer'}
                    placeholderText={fields?.addressCtaPlaceholderText}
                    showShadow={false}
                  />
                </Box>
              </Flex>
            </Flex>
            <Flex
              id="inline-video"
              width={['100%', null, '50%', '55%']}
              alignItems="center"
              justifyContent={['center', null, 'flex-end']}
              minWidth={[null, null, '350px', null]}
            >
              <Flex
                position="relative"
                height={['431px', '543px', 'unset']}
                width="100%"
                maxWidth={['582px', null, 'unset']}
                alignItems="flex-end"
              >
                <Video
                  initialPoster={fields?.videoInitialImage}
                  initialPosterDesktop={fields?.videoInitialImageDesktop}
                  preload="auto"
                  autoPlay
                  muted
                  loop
                  playsInline
                  // CSS trick from: https://www.cookieshq.co.uk/posts/responsive-video-poster-images-with-css-and-activestorage
                  // use transparent GIF (base64 encode of 1x1px transparent GIF) for the
                  // poster image to prevent browser from picking a frame of the video.
                  // we then set the background image using CSS (see how initialPoster prop is used)
                  poster="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
                  src="https://odfil.es/static/videos/videohero-autoplay.mp4"
                />
                <Button
                  position="absolute"
                  aria-label="open-how-it-works-video-modal"
                  analyticsName={`cosmos-landing-page-open-video-modal-${fields?.analyticsId}`}
                  transform={['translate(-15%, 0px)', 'translate(-50%, -20%)']}
                  left={['30%', '50%']}
                  top="50%"
                  zIndex="7"
                  onClick={openVideoModalHandler}
                  variant="link"
                >
                  <Flex
                    px={5}
                    py={4}
                    alignItems="center"
                    style={{
                      backgroundColor: 'rgba(41, 40, 39, 0.64)',
                      borderRadius: '18px',
                    }}
                    backdropFilter="blur(20px)"
                  >
                    <Flex mr={3} alignItems="center">
                      <NextImage
                        layout="fixed"
                        width="14px"
                        height="14px"
                        loader={imgixLoader}
                        src="2023-06-1686866816613-88291.svg"
                        alt="Video play icon"
                      />
                    </Flex>
                    <Text
                      m={0}
                      fontWeight="semibold"
                      color="neutrals0"
                      size="110"
                      transform="translateY(1px)"
                    >
                      How it works
                    </Text>
                  </Flex>
                </Button>
              </Flex>
            </Flex>
            <Flex
              display={['none', null, null, 'block']}
              justifyContent="center"
              left="50%"
              transform="translate(-50%, -36px)"
              position="absolute"
              bottom="0"
            >
              <PressBox />
            </Flex>
          </Flex>
        </Box>
        <Flex
          justifyContent="center"
          gridColumn={gridStyles.colSpan12}
          mt={9}
          display={['block', null, null, 'none']}
        >
          <PressBox />
        </Flex>
      </Grid>
    </Box>
  );
};

const PressBox = () => {
  return (
    <Flex justifyContent="center">
      <Flex
        flexWrap="wrap"
        columnGap={[4, 5, 6]}
        p={[4, 5, 6]}
        alignItems="center"
        justifyContent="center"
        style={{
          borderRadius: '20px',
          backgroundColor: 'rgba(242, 241, 240, 0.64)',
          backdropFilter: 'blur(20px)',
        }}
      >
        <Text
          textAlign={['center', 'unset']}
          width={['100%', 'unset']}
          fontSize={['s00', null, 's1']}
          style={{
            color: '#8F8E8D',
          }}
          css={css`
            @media (min-width: 375px) and (max-width: 562px) {
              width: 100%;
              text-align: center;
            }
          `}
        >
          Featured in:
        </Text>
        <Flex
          maxWidth={['240px', 'unset']}
          rowGap={3}
          columnGap={[4, 5, 6]}
          flexWrap="wrap"
          alignItems="center"
          justifyContent={['space-between', 'center']}
          css={css`
            @media (min-width: 375px) and (max-width: 562px) {
              max-width: 280px;
              justify-content: space-between;
            }
          `}
        >
          <Box
            width={['60px', '64px', '75px']}
            height="auto"
            order={[1, null, 2]}
            css={css`
              @media (min-width: 375px) and (max-width: 562px) {
                order: 1;
              }
            `}
          >
            {generateDeprecatedImgixPictureSet({
              // Forbes logo
              photoUrl: 'https://imgdrop.imgix.net/2023-07-1690500903565-83391.png',
              sizes: generateDeprecatedPictureSizeSet([
                { w: 60, h: 16 },
                { w: 60, h: 16 },
                { w: 64, h: 18 },
                { w: 75, h: 20 },
                { w: 75, h: 20 },
              ]),
              debugImageSizes: false,
              radii: {},
              lazy: true,
            })}
          </Box>
          <Box width={['60px', '70px', '78px']} order={[2, null, 4]}>
            {generateDeprecatedImgixPictureSet({
              // Fortune logo
              photoUrl: 'https://imgdrop.imgix.net/2023-07-1690500903560-61291.png',
              sizes: generateDeprecatedPictureSizeSet([
                { w: 60, h: 16 },
                { w: 60, h: 16 },
                { w: 64, h: 18 },
                { w: 75, h: 20 },
                { w: 75, h: 20 },
              ]),
              debugImageSizes: false,
              radii: {},
              lazy: true,
            })}
          </Box>
          <Box width={['125px', '132px', '155px']} order={[3, null, 1]}>
            {generateDeprecatedImgixPictureSet({
              // New York Times logo
              photoUrl: 'https://imgdrop.imgix.net/2023-07-1690500903555-88900.png',
              sizes: generateDeprecatedPictureSizeSet([
                { w: 125, h: 16 },
                { w: 126, h: 16 },
                { w: 140, h: 18 },
                { w: 145, h: 20 },
                { w: 145, h: 20 },
              ]),
              debugImageSizes: false,
              radii: {},
              lazy: true,
            })}
          </Box>
          <Box width={['30px', '35px', '38px']} order={[4, null, 3]}>
            {generateDeprecatedImgixPictureSet({
              // Wall Street Journal logo
              photoUrl: 'https://imgdrop.imgix.net/2023-07-1690500903562-10942.png',
              sizes: generateDeprecatedPictureSizeSet([
                { w: 125, h: 16 },
                { w: 126, h: 16 },
                { w: 140, h: 18 },
                { w: 145, h: 20 },
                { w: 145, h: 20 },
              ]),
              debugImageSizes: false,
              radii: {},
              lazy: true,
            })}
          </Box>
        </Flex>
      </Flex>
    </Flex>
  );
};

const VideoHero: EntryComponent<ILpComponentVideoHero> = {
  render: RenderVideoHeroCard,
};

export default VideoHero;
